import React, { useCallback, useEffect, useMemo, useRef, useState, useTransition } from "react";

import moment from "moment";

import {
  ArrayUtils,
  clearLocalStorageAllFieldsDataTableFilters,
  createFieldSearchMatcher,
  getLocalStorageAllFieldsDataTableFilters,
  getLocalStorageFieldFilterOptions,
  getLocalStorageFieldSearchQuery,
  getLocalStorageLastViewedYear,
  isFeatureActive,
  isFieldEnrolled,
  setLocalStorageAllFieldsDataTableFilters,
  setLocalStorageFieldFilterOptions,
  setLocalStorageFieldSearchQuery,
  setLocalStorageLastViewedYear,
  useDebouncedState,
  useFeatureFlag,
  useGetAllCarbonProgramOutcomes,
  useGetAllFieldPracticesByFarm,
  useGetFarmFeatureFlags,
  useGetFarmReportingYear,
  useGetFarms,
  useGetFieldPracticeFilterOptions,
  useGetFieldsByFarmId,
  useGetPracticeCrops,
  useGetPracticeMineralFertilizers,
  useGetPracticeOrganicFertilizers,
  useGetPracticeSeedings,
  useGetPracticeTillages,
  useNavigate,
  useTranslation,
} from "utils";

import { ALL_FIELDS_ROUTE, FEATURE_FLAGS, FEATURE_FLAG_SLUGS } from "consts";

import { CropFCPO, ENROLLMENT_TYPE } from "models";

import { Button, CropFilterChip, Drawer, EmptyStateCard, FieldSearchFilterBar, Flex, MainPage, Spin } from "components";

import { FarmSwitcher } from "pages/app/all-field-view/components/FarmSwitcher";
import { DATA_STATUS } from "pages/app/all-field-view/components/FieldFilterOptions";
import { FIELD_SORT_OPTION, FieldSortOptions } from "pages/app/all-field-view/components/FieldSortOptions";
import { FieldToolbarAddMenu } from "pages/app/all-field-view/components/FieldToolbarAddMenu";
import { useSubmissionStatus } from "pages/app/all-field-view/hooks/useSubmissionStatus";
import { getAllCropsPracticeDataByYear, getEnrolledFieldsCount, sortFieldsBySortOption } from "pages/app/all-field-view/utils/AllFieldsUtils";
import { AllFieldsDataStatusFilter } from "pages/app/all-fields-data-table-view/components/AllFieldsDataStatusFilter";
import { AllFieldsDataTable } from "pages/app/all-fields-data-table-view/components/AllFieldsDataTable";
import { AllFieldsDataTableFilters, type AllFieldsDataTableFiltersState } from "pages/app/all-fields-data-table-view/components/AllFieldsDataTableFilters";
import { AllFieldsHarvestYearFilter } from "pages/app/all-fields-data-table-view/components/AllFieldsHarvestYearFilter";
import { AllFieldsImpactPanel } from "pages/app/all-fields-data-table-view/components/AllFieldsImpactPanel";
import {
  getAddCropYearWindow,
  getFieldIdsWithMissingDataForYear,
  getYearsWithMissingData,
  rebucketMainCropsByCalendarHarvestYear,
  rebucketSideCropsByRelatedMainCropYear,
} from "pages/app/all-fields-data-table-view/utils/AllFieldsDataTableUtils";
import { FieldEnrollmentDrawer } from "pages/app/fields/components/FieldEnrollmentDrawer";
import { FieldUpload } from "pages/app/fields/components/FieldUpload";
import { MainLoadingSkeleton } from "pages/app/historical-practice-data-table-view/components/LoadingSkeletons";
import { useTableBodyHeight } from "pages/app/historical-practice-data-table-view/hooks/useTableBodyHeight";

const SEARCH_DEBOUNCE_MS = 300;

const DEFAULT_FILTERS: AllFieldsDataTableFiltersState = {
  appliedCropIds: [],
  previouslyAppliedCropIds: [],
  missingGrowingPeriod: false,
  missingSeedings: false,
  missingTillages: false,
  missingOrganicFertilizers: false,
  missingMineralFertilizers: false,
  missingYield: false,
};

export const AllFieldsDataTableView = () => {
  const { t } = useTranslation(["allFieldsDataTableView", "historicalPracticeDataTableView", "fieldTimeline", "allFieldView"]);
  const navigate = useNavigate();
  const { currentFarm, isLoading: isFarmsLoading } = useGetFarms();
  const carbonProgram = currentFarm?.carbonProgram ?? null;
  const {
    data: carbonProgramOutcomes,
    progress,
    isLoading: isCarbonProgramOutcomesLoading,
    isFetching: isFetchingCarbonProgramOutcomes,
  } = useGetAllCarbonProgramOutcomes(currentFarm?.id, carbonProgram);
  const { data: fields, isLoading: isFieldsLoading } = useGetFieldsByFarmId({ farmId: currentFarm?.id ?? "" });
  const {
    data: allFieldsPractices,
    isLoading: isAllFieldPracticesLoading,
    isFetching: isFetchingAllFieldsPractices,
  } = useGetAllFieldPracticesByFarm(currentFarm);
  const {
    data: { activeFarmReportingYear },
    isLoading: isFarmReportingYearLoading,
  } = useGetFarmReportingYear(currentFarm?.id || null);
  const { isLoading: isSeedingPracticesLoading } = useGetPracticeSeedings({ enabled: false });
  const { isLoading: isTillagePracticesLoading } = useGetPracticeTillages({ enabled: false });
  const { isLoading: isOrganicFertilizerPracticesLoading } = useGetPracticeOrganicFertilizers({ enabled: false });
  const { isLoading: isMineralFertilizerPracticesLoading } = useGetPracticeMineralFertilizers({ enabled: false });
  const { data: practiceCrops } = useGetPracticeCrops({});
  const { data: fieldPracticeFilterOptions, isLoading: isFieldPracticeFilterOptionsLoading } = useGetFieldPracticeFilterOptions({
    farmId: currentFarm?.id,
  });
  const { data: farmFeatureFlags } = useGetFarmFeatureFlags({ farmId: currentFarm?.id ?? "" });
  const isFarmFeatureFlagsLoading = !!currentFarm?.id && !farmFeatureFlags;
  const { isEnabled: isSoilSightEnabled } = useFeatureFlag(FEATURE_FLAGS.SOIL_SIGHT_INTEGRATION);
  const isSoilPlus3RewardsFeatureActive = isFeatureActive(FEATURE_FLAG_SLUGS.SOIL_PLUS_3_REWARDS, farmFeatureFlags);
  const isSoilPlus3DataGapFeatureActive = isFeatureActive(FEATURE_FLAG_SLUGS.SOIL_PLUS_3_DATA_GAP, farmFeatureFlags);
  const { isDataSubmitted, isDataSubmissionReopened, submissionStatusValue } = useSubmissionStatus(activeFarmReportingYear);
  const isAutoEnrollmentType = carbonProgram?.fieldsManagement?.fieldEnrollmentType === ENROLLMENT_TYPE.auto;

  const [selectedYear, setSelectedYear] = useState<number>();
  const [pendingYear, setPendingYear] = useState<number>();
  const [isYearSwitching, startYearSwitch] = useTransition();
  const [dataStatusFilter, setDataStatusFilter] = useState<DATA_STATUS>(DATA_STATUS.ALL);
  const [filters, setFilters] = useState<AllFieldsDataTableFiltersState>(DEFAULT_FILTERS);
  const [searchQuery, setSearchQuery] = useState(() => (currentFarm?.id ? getLocalStorageFieldSearchQuery(currentFarm.id) : ""));
  // FieldSortOptions restores/persists this itself from the shared fieldSortOption key on mount and on change
  const [appliedSortOption, setAppliedSortOption] = useState<FIELD_SORT_OPTION | null>(null);
  const [showFieldUploadDrawer, setShowFieldUploadDrawer] = useState<boolean>(false);
  const [showBulkEnrollDrawer, setShowBulkEnrollDrawer] = useState<boolean>(false);
  const [showMoreFilters, setShowMoreFilters] = useState<boolean>(false);

  const isPracticesDataLoading =
    isSeedingPracticesLoading || isTillagePracticesLoading || isOrganicFertilizerPracticesLoading || isMineralFertilizerPracticesLoading;
  // selectedYear defaults even when the farm has no crop data anywhere yet (see the effect below), so the table
  // can still show a per-field "Add crop" placeholder row for that year (PE-821) instead of a dead end
  const isTableDataLoading =
    isFarmsLoading ||
    isFieldsLoading ||
    isCarbonProgramOutcomesLoading ||
    isAllFieldPracticesLoading ||
    isFarmReportingYearLoading ||
    isPracticesDataLoading ||
    isFieldPracticeFilterOptionsLoading ||
    (selectedYear === undefined && !!currentFarm?.id);

  // once loaded, keeps the table mounted through background refetches (e.g. a practice-participation toggle
  // invalidating queries) so open drawers/local state in AllFieldsDataTable survive instead of getting unmounted
  const [loadedTableDataForFarmId, setLoadedTableDataForFarmId] = useState<string | null>(null);
  useEffect(() => {
    if (!isTableDataLoading && currentFarm?.id) setLoadedTableDataForFarmId(currentFarm.id);
  }, [isTableDataLoading, currentFarm?.id]);
  const isInitialTableDataLoad = isTableDataLoading && loadedTableDataForFarmId !== currentFarm?.id;

  const hasNoFields = (fields?.length ?? currentFarm?.fieldCount ?? 0) === 0;

  const hasAnyMissingFilterEnabled =
    filters.missingGrowingPeriod ||
    filters.missingOrganicFertilizers ||
    filters.missingMineralFertilizers ||
    filters.missingSeedings ||
    filters.missingTillages ||
    filters.missingYield;
  // stays visible whenever a missing-type checkbox is on, even if the user collapses "More filters" afterwards
  const isMissingFiltersPanelVisible = showMoreFilters || hasAnyMissingFilterEnabled;

  const hasAnyFiltersApplied =
    filters.appliedCropIds.length > 0 ||
    filters.previouslyAppliedCropIds.length > 0 ||
    hasAnyMissingFilterEnabled ||
    dataStatusFilter !== DATA_STATUS.ALL ||
    !!searchQuery;

  const handleClearAll = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setDataStatusFilter(DATA_STATUS.ALL);
    setSearchQuery("");
    if (currentFarm?.id) {
      clearLocalStorageAllFieldsDataTableFilters();
      setLocalStorageFieldSearchQuery(currentFarm.id, "");
      const existing = getLocalStorageFieldFilterOptions();
      setLocalStorageFieldFilterOptions({
        farmId: currentFarm.id,
        cropIds: [],
        years: existing?.farmId === currentFarm.id ? existing.years ?? [] : [],
        dataStatus: existing?.farmId === currentFarm.id ? existing.dataStatus ?? DATA_STATUS.ALL : DATA_STATUS.ALL,
      });
    }
  }, [currentFarm?.id]);

  const allCropsPracticeDataByYear = useMemo(
    () =>
      rebucketMainCropsByCalendarHarvestYear(
        rebucketSideCropsByRelatedMainCropYear(getAllCropsPracticeDataByYear(fields ?? [], carbonProgram, allFieldsPractices))
      ),
    [fields, carbonProgram, allFieldsPractices]
  );

  // farm-wide (not year-scoped) crop options, same source Timeline's crop filter uses, so the two views offer
  // identical crop choices regardless of which one a farmer last used
  const mainCropOptions = useMemo(
    () => (practiceCrops ?? []).filter((crop) => crop.isMainCrop && fieldPracticeFilterOptions?.cropIds.includes(crop.id)),
    [practiceCrops, fieldPracticeFilterOptions]
  );
  const coverCropOptions = useMemo(
    () => (practiceCrops ?? []).filter((crop) => !crop.isMainCrop && !crop.isUndersowing && fieldPracticeFilterOptions?.cropIds.includes(crop.id)),
    [practiceCrops, fieldPracticeFilterOptions]
  );
  // previous-crop tab has no farm-wide equivalent on the backend, so it stays derived from the crop rotation
  // data already loaded for the table (spans every year, not just the selected one)
  const previousCropOptions = useMemo(() => {
    const allYearEntries = Object.values(allCropsPracticeDataByYear).flat();
    const uniquePreviousCrops = ArrayUtils.uniqueBy(
      allYearEntries.map((entry) => entry.previousMainCrop).filter((crop): crop is CropFCPO => !!crop),
      (crop) => crop.cropId
    );

    return uniquePreviousCrops
      .map((crop) => {
        const name = practiceCrops?.find((practiceCrop) => practiceCrop.id === crop.cropId)?.name;
        return name ? { id: crop.cropId, name } : null;
      })
      .filter((option): option is { id: string; name: string } => !!option);
  }, [allCropsPracticeDataByYear, practiceCrops]);

  const handleApplyCropFilters = (cropIds: string[], previousCropIds: string[]) => {
    setFilters((prev) => ({ ...prev, appliedCropIds: cropIds, previouslyAppliedCropIds: previousCropIds }));
    if (!currentFarm?.id) return;
    // main/cover crop selection is single-sourced from this shared key so it stays in sync with Timeline/Map
    // view — previousCropIds is intentionally never persisted here, see AllFieldsDataTableFiltersData
    const existing = getLocalStorageFieldFilterOptions();
    setLocalStorageFieldFilterOptions({
      farmId: currentFarm.id,
      cropIds,
      years: existing?.farmId === currentFarm.id ? existing.years ?? [] : [],
      dataStatus: existing?.farmId === currentFarm.id ? existing.dataStatus ?? DATA_STATUS.ALL : DATA_STATUS.ALL,
    });
  };

  // same server-driven year list Timeline's harvest-year filter uses, so the two stay consistent — not the
  // client-computed allCropsPracticeDataByYear keys, which is a separate (and potentially narrower) set
  const dataYears = useMemo(() => [...(fieldPracticeFilterOptions?.years ?? [])].sort((a, b) => a - b), [fieldPracticeFilterOptions]);

  // crop filters have nothing to filter by when there's no crop data at all, even if fields exist
  const hasNoCropDataToFilter = hasNoFields || dataYears.length === 0;

  // the filter only offers years a crop could actually be added for (Timeline's own addable-year window), plus
  // any year that already has real data, even if that year falls outside the window, so it's never hidden
  const availableYears = useMemo(() => {
    if (activeFarmReportingYear === undefined) return [];

    const { startYear, endYear } = getAddCropYearWindow(activeFarmReportingYear, carbonProgram?.fieldsManagement.monitoringPeriodType);
    const windowYears = Array.from({ length: endYear - startYear + 1 }, (_, index) => startYear + index);
    const dataYearsOutsideWindow = dataYears.filter((year) => year < startYear || year > endYear);
    return Array.from(new Set([...windowYears, ...dataYearsOutsideWindow])).sort((a, b) => a - b);
  }, [activeFarmReportingYear, carbonProgram?.fieldsManagement.monitoringPeriodType, dataYears]);

  const yearsWithMissingData = useMemo(() => {
    if (activeFarmReportingYear === undefined) return new Set<number>();
    return getYearsWithMissingData(
      availableYears,
      activeFarmReportingYear,
      fields,
      allCropsPracticeDataByYear,
      allFieldsPractices,
      carbonProgram,
      practiceCrops
    );
  }, [availableYears, activeFarmReportingYear, fields, allCropsPracticeDataByYear, allFieldsPractices, carbonProgram, practiceCrops]);

  // toolbar "+" menu's overview stat — Table-view-local, scoped to the currently selected year only
  const fieldsWithOpenTasksCount = useMemo(() => {
    if (selectedYear === undefined || activeFarmReportingYear === undefined) return 0;
    return getFieldIdsWithMissingDataForYear(
      selectedYear,
      fields,
      activeFarmReportingYear,
      allCropsPracticeDataByYear,
      allFieldsPractices,
      carbonProgram,
      practiceCrops
    ).size;
  }, [selectedYear, activeFarmReportingYear, fields, allCropsPracticeDataByYear, allFieldsPractices, carbonProgram, practiceCrops]);

  // reset every filter when the farmer switches farms (via FarmSwitcher) — otherwise the previous farm's search,
  // sort, crop/status/missing-type filters, and selected year would silently carry over and apply to the new
  // farm's data. FieldSortOptions restores its own appliedSortOption per farmId already, so it's untouched here.
  // Resetting selectedYear to undefined re-triggers the restore effect below with the new farm's stored filters.
  const previousFarmIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!currentFarm?.id || previousFarmIdRef.current === currentFarm.id) {
      previousFarmIdRef.current = currentFarm?.id;
      return;
    }
    previousFarmIdRef.current = currentFarm.id;
    setSelectedYear(undefined);
    setDataStatusFilter(DATA_STATUS.ALL);
    setFilters(DEFAULT_FILTERS);
    setSearchQuery(getLocalStorageFieldSearchQuery(currentFarm.id));
    setShowMoreFilters(false);
  }, [currentFarm?.id]);

  // seed the selected year + data status + main/cover crop selection from the shared fieldFilterOptions key
  // (single-element years array) so they stay in sync with Timeline/Map view, and the missing-type checkboxes
  // from this table's own key — previouslyAppliedCropIds is deliberately left at its DEFAULT_FILTERS value ([])
  // here, since it's never persisted (see AllFieldsDataTableFiltersData). Defaults from dataYears, not the wider
  // availableYears range, so it lands on a year with real data rather than an arbitrary placeholder year.
  useEffect(() => {
    if (!currentFarm?.id || selectedYear !== undefined || isFarmReportingYearLoading) return;

    if (dataYears.length === 0) {
      setSelectedYear(getLocalStorageLastViewedYear(currentFarm.id) ?? activeFarmReportingYear ?? moment().year());
      return;
    }

    const storedFieldFilterOptions = getLocalStorageFieldFilterOptions();
    const lastViewedYear = getLocalStorageLastViewedYear(currentFarm.id);
    if (lastViewedYear !== undefined) {
      setSelectedYear(lastViewedYear);
    } else {
      const storedYear = storedFieldFilterOptions?.years?.[storedFieldFilterOptions.years.length - 1];
      setSelectedYear(storedYear && dataYears.includes(storedYear) ? storedYear : dataYears[0]);
    }
    if (storedFieldFilterOptions?.dataStatus) setDataStatusFilter(storedFieldFilterOptions.dataStatus);

    const storedTableFilters = getLocalStorageAllFieldsDataTableFilters(currentFarm.id);
    const { farmId: _farmId, ...missingDataState } = storedTableFilters ?? {};
    setFilters((prev) => ({
      ...prev,
      ...missingDataState,
      appliedCropIds: storedFieldFilterOptions?.farmId === currentFarm.id ? storedFieldFilterOptions.cropIds : [],
    }));
  }, [currentFarm?.id, dataYears, selectedYear, isFarmReportingYearLoading, activeFarmReportingYear]);

  // the chip shows the picked year at once, while the table switches in a transition (the old year's table stays under a loader)
  const displayedYear = isYearSwitching && pendingYear !== undefined ? pendingYear : selectedYear;
  // lastViewedYear (not fieldFilterOptions, a separate multi-select filter) is what carries this over to Timeline
  const handleSelectYear = (year: number) => {
    const tableBody = tableWrapperRef.current?.querySelector(".ant-table-tbody-virtual-holder");
    if (tableBody) tableBody.scrollTop = 0;
    setPendingYear(year);
    startYearSwitch(() => setSelectedYear(year));
    if (currentFarm?.id) setLocalStorageLastViewedYear(currentFarm.id, year);
  };

  const handleDataStatusChange = (status: DATA_STATUS) => {
    setDataStatusFilter(status);
    if (!currentFarm?.id) return;
    const existing = getLocalStorageFieldFilterOptions();
    setLocalStorageFieldFilterOptions({
      farmId: currentFarm.id,
      cropIds: existing?.farmId === currentFarm.id ? existing.cropIds : [],
      years: existing?.farmId === currentFarm.id ? existing.years ?? [] : [],
      dataStatus: status,
    });
  };

  useEffect(() => {
    if (!currentFarm?.id) return;
    const { appliedCropIds: _appliedCropIds, previouslyAppliedCropIds: _previouslyAppliedCropIds, ...missingDataFilters } = filters;
    setLocalStorageAllFieldsDataTableFilters({ farmId: currentFarm.id, ...missingDataFilters });
  }, [filters, currentFarm?.id]);

  useEffect(() => {
    if (!currentFarm?.id) return;
    setLocalStorageFieldSearchQuery(currentFarm.id, searchQuery);
  }, [searchQuery, currentFarm?.id]);

  const debouncedSearchQuery = useDebouncedState(searchQuery, SEARCH_DEBOUNCE_MS);
  const matchesSearch = useMemo(() => createFieldSearchMatcher(debouncedSearchQuery), [debouncedSearchQuery]);
  const searchFilteredFields = useMemo(() => fields?.filter(matchesSearch) ?? [], [fields, matchesSearch]);

  const enrolledFieldIds = useMemo(() => {
    const enrolledIds = new Set<string>();
    Object.entries(allFieldsPractices || {}).forEach(([fieldId, fieldPracticeResponse]) => {
      if (isFieldEnrolled(fieldPracticeResponse?.availablePracticeBundles ?? [], carbonProgram)) enrolledIds.add(fieldId);
    });
    return enrolledIds;
  }, [allFieldsPractices, carbonProgram]);

  // AllFieldsDataTable orders its rows by the position of their field within this array, so sorting it here is
  // enough to make the (unchanged) FieldSortOptions options apply to the table
  const sortedFields = useMemo(
    () => sortFieldsBySortOption(searchFilteredFields, appliedSortOption, enrolledFieldIds),
    [searchFilteredFields, appliedSortOption, enrolledFieldIds]
  );

  // re-run when table content is ready (selectedYear + data for that year) so header height is measured after the table has rendered
  const tableDataLength = selectedYear ? allCropsPracticeDataByYear[selectedYear]?.length ?? -1 : -1;
  const { tableWrapperRef, tableBodyHeight } = useTableBodyHeight({
    selectedYear,
    tableDataLength,
    isWrapperHidden: isInitialTableDataLoad,
  });

  const navigationCrumbs = {
    crumbs: [
      { name: "", content: <FarmSwitcher />, replacedByFarmSwitch: true },
      { name: t("fieldTimeline:header.fieldManagementCrumb"), link: ALL_FIELDS_ROUTE },
    ],
    additionalData: <></>,
  };

  const enrolledFieldsCount = getEnrolledFieldsCount(fields ?? [], carbonProgramOutcomes ?? {}, carbonProgram, allFieldsPractices);
  const shouldShowInsightsImpactPanel = isSoilSightEnabled && !!carbonProgram?.platformFeatures?.fieldManagement?.insightsImpactPanel;
  const shouldShowSubmissionSuccessModalContent = isDataSubmitted && !isDataSubmissionReopened;
  const fieldSizes = useMemo(() => fields?.reduce((sizes: Record<string, number>, field) => ({ ...sizes, [field.id]: field.size ?? 0 }), {}) || {}, [fields]);
  // the farm's own registered size is just the farmer's commercial reference and doesn't cap how many fields they
  // add, so the % denominator has to be every field's own actual size instead — that way no filter is applied
  // shows 100% by construction, and a filter's % is genuinely "share of the farm's actual mapped area"
  const allFieldsTotalHectares = Object.values(fieldSizes).reduce((total, size) => total + size, 0);

  return (
    <MainPage
      fullWidth
      pageName="all-fields-data-table-view-page historical-practice-data-table-view-page"
      navigationCrumbs={navigationCrumbs}
      showMobileAlert
      share={{ title: t("allFieldView:allFieldView") }}
    >
      <Flex
        vertical
        className="all-fields-data-table-view historical-practice-data-table-view container"
        data-testid="all-fields-data-table-view-page-container"
        gap="1rem"
      >
        {!isInitialTableDataLoad && (
          <Flex vertical>
            <FieldSearchFilterBar
              searchQuery={searchQuery}
              onSearchQueryChange={setSearchQuery}
              searchDisabled={!fields?.length}
              leadingActions={
                <FieldToolbarAddMenu
                  fields={fields}
                  carbonProgram={carbonProgram}
                  isSoilPlus3DataGapFeatureActive={isSoilPlus3DataGapFeatureActive}
                  isDataSubmitted={isDataSubmitted}
                  fieldsWithOpenTasksCount={fieldsWithOpenTasksCount}
                  onAddFields={() => setShowFieldUploadDrawer(true)}
                  onEnrollFields={() => setShowBulkEnrollDrawer(true)}
                />
              }
              filters={
                <Flex align="center" gap="1rem">
                  <FieldSortOptions
                    appliedSortOption={appliedSortOption}
                    setAppliedSortOption={setAppliedSortOption}
                    farmId={currentFarm?.id || ""}
                    isDisabled={!fields?.length}
                    isAutoEnrollmentType={isAutoEnrollmentType}
                  />
                  <Flex align="center" gap="0.5rem">
                    {availableYears.length > 0 && (
                      <AllFieldsHarvestYearFilter
                        availableYears={availableYears}
                        selectedYear={displayedYear}
                        onSelectYear={handleSelectYear}
                        isDisabled={hasNoFields}
                        yearsWithMissingData={yearsWithMissingData}
                      />
                    )}
                    <CropFilterChip
                      mainCropOptions={mainCropOptions}
                      coverCropOptions={coverCropOptions}
                      previousCropOptions={previousCropOptions}
                      appliedCropIds={filters.appliedCropIds}
                      appliedPreviousCropIds={filters.previouslyAppliedCropIds}
                      onApply={handleApplyCropFilters}
                      isDisabled={hasNoCropDataToFilter}
                    />
                    <AllFieldsDataStatusFilter dataStatusFilter={dataStatusFilter} onChange={handleDataStatusChange} isDisabled={hasNoCropDataToFilter} />
                    <Button type="text" size="small" onClick={() => setShowMoreFilters((prev) => !prev)} disabled={hasNoCropDataToFilter}>
                      {isMissingFiltersPanelVisible ? t("allFieldsDataTableView:filters.lessFilters") : t("allFieldsDataTableView:filters.moreFilters")}
                    </Button>
                  </Flex>
                </Flex>
              }
            />
            {isMissingFiltersPanelVisible && (
              <Flex align="center" className="historical-practice-data-table-view-filters">
                <AllFieldsDataTableFilters
                  allCropsPracticeDataByYear={allCropsPracticeDataByYear}
                  carbonProgram={carbonProgram}
                  selectedYear={selectedYear}
                  filters={filters}
                  setFilters={setFilters}
                  onClearAll={handleClearAll}
                  hasNoFields={hasNoFields}
                />
              </Flex>
            )}
          </Flex>
        )}
        <Flex className="historical-practice-data-table-view-main-container" justify="space-between">
          {isInitialTableDataLoad ? (
            <MainLoadingSkeleton />
          ) : (
            <div
              className={`historical-practice-data-table-view-table${isYearSwitching ? " all-fields-data-table-view-year-switching" : ""}`}
              ref={tableWrapperRef}
            >
              {isYearSwitching && (
                <Flex className="all-fields-data-table-view-year-loader" justify="center" data-testid="all-fields-year-switch-loader">
                  <Spin size="large" />
                </Flex>
              )}
              {hasNoFields ? (
                <Flex className="timeline-empty-state" align="center" justify="center">
                  <EmptyStateCard
                    iconName="klim/field"
                    title={t("allFieldView:noFields.title")}
                    description={t("allFieldView:noFields.description")}
                    buttonText={t("allFieldView:fieldContextMenu.addFields")}
                    onButtonClick={() => setShowFieldUploadDrawer(true)}
                  />
                </Flex>
              ) : (
                <AllFieldsDataTable
                  allCropsPracticeDataByYear={allCropsPracticeDataByYear}
                  allFieldsPractices={allFieldsPractices}
                  carbonProgram={carbonProgram}
                  selectedYear={selectedYear}
                  fields={sortedFields}
                  filters={filters}
                  dataStatusFilter={dataStatusFilter}
                  tableBodyHeight={tableBodyHeight}
                  hasFiltersActive={hasAnyFiltersApplied}
                  onClearFilters={handleClearAll}
                  farmId={currentFarm?.id}
                  totalFieldsCount={fields?.length ?? currentFarm?.fieldCount ?? 0}
                  carbonProgramOutcomes={carbonProgramOutcomes}
                  isDataSubmitted={isDataSubmitted}
                  isDataSubmissionReopened={isDataSubmissionReopened}
                  lastSubmissionDate={submissionStatusValue?.lastSubmissionDate}
                  lastSubmissionYear={submissionStatusValue?.lastSubmissionYear}
                  fieldSizes={fieldSizes}
                  allFieldsTotalHectares={allFieldsTotalHectares}
                  isEnrollmentDataLoading={isFetchingAllFieldsPractices}
                />
              )}
            </div>
          )}
          <AllFieldsImpactPanel
            isHidden={!carbonProgram?.id || hasNoFields || (!shouldShowInsightsImpactPanel && !carbonProgram?.platformFeatures?.fieldManagement?.impactPanel)}
            isLoading={isFarmReportingYearLoading || isFarmFeatureFlagsLoading}
            reportingYear={activeFarmReportingYear ?? new Date().getFullYear()}
            farmId={currentFarm?.id ?? ""}
            carbonProgram={carbonProgram}
            carbonProgramOutcomes={carbonProgramOutcomes}
            progress={progress}
            fieldSizes={fieldSizes}
            allFieldsPractices={allFieldsPractices}
            isFetchingOutcomes={isFetchingCarbonProgramOutcomes}
            isFetchingPractices={isFetchingAllFieldsPractices}
            enrolledFieldsCount={enrolledFieldsCount}
            shouldShowInsightsImpactPanel={shouldShowInsightsImpactPanel}
            shouldShowSoilPlus3ImpactPanel={isSoilPlus3RewardsFeatureActive}
            shouldShowSubmissionSuccessModalContent={shouldShowSubmissionSuccessModalContent}
            onOpenBulkEnrollDrawer={() => setShowBulkEnrollDrawer(true)}
            onNavigateToMissingData={() => navigate(ALL_FIELDS_ROUTE, { state: { openTasksDrawer: true } })}
          />
        </Flex>
      </Flex>
      <FieldEnrollmentDrawer fields={fields ?? []} visible={showBulkEnrollDrawer} onClose={() => setShowBulkEnrollDrawer(false)} />
      <Drawer
        className="field-upload-drawer"
        visible={showFieldUploadDrawer}
        title={t("allFieldView:titleUpdateDrawer")}
        onClose={() => setShowFieldUploadDrawer(false)}
      >
        <FieldUpload onFinish={() => setShowFieldUploadDrawer(false)} />
      </Drawer>
    </MainPage>
  );
};
